import * as signalR from "@microsoft/signalr"
import type { MessageDto, ConversationDto, NewConversationDto, MarkAsReadResultDto } from "../types/message"
import { ChannelMessageDto } from "../types/channelMessage"
import type { GlobalMessageDto } from "../types/globalMessage"
import { SIGNALR_URL } from "../utils/ApiLinks"
import { GlobalUser } from "../types/UserToken"
import { ChannelService } from "./channelService"
import { UserConnection } from "../types/channel"

type MessageHandler = (message: MessageDto) => void
type ConversationHandler = (conversation: ConversationDto) => void
type ConversationsHandler = (conversations: ConversationDto[]) => void
type ReadReceiptHandler = (data: MarkAsReadResultDto) => void
type JoinedConversationHandler = (conversationId: number) => void

type ActiveUsersHandler = (userIds: number[]) => void


type ChannelMessageHandler = (message: ChannelMessageDto) => void
type ChannelReadReceiptHandler = (data: { messageId: number, readerProfileId: number }) => void
type GlobalMessageHandler = (message: GlobalMessageDto) => void

let connection: signalR.HubConnection | null = null
let connectionPromise: Promise<boolean> | null = null
let isConnecting = false
let lastUserIds: number[] = []

const messageListeners: MessageHandler[] = []
const newConversationListeners: ConversationHandler[] = []
const receiveConversationsListeners: ConversationsHandler[] = []
const readReceiptListeners: ReadReceiptHandler[] = []
const joinedConversationListeners: JoinedConversationHandler[] = []

const activeUsersListeners: ActiveUsersHandler[] = []


const channelMessageListeners: ChannelMessageHandler[] = []
const channelReadReceiptListeners: ChannelReadReceiptHandler[] = []
const globalMessageListeners: GlobalMessageHandler[] = []

const setupEventHandlers = (conn: signalR.HubConnection): void => {
  conn.on("ReceiveMessage", (message: MessageDto) => {
    messageListeners.forEach((listener) => listener(message))
  })

  conn.on("NewConversation", (conversation: ConversationDto) => {
    newConversationListeners.forEach((listener) => listener(conversation))
  })

  conn.on("ReceiveConversations", (conversations: ConversationDto[]) => {
    const conversationIds = conversations.map((c) => c.conversationId)

    // Usar conversationIds para logging o depuración
    console.debug(`Received ${conversationIds.length} conversations: ${conversationIds.join(', ')}`)

    conversations.forEach((conv) => {
    })

    receiveConversationsListeners.forEach((listener) => listener(conversations))

  })

  conn.on("MessageRead", (data: MarkAsReadResultDto) => {
    readReceiptListeners.forEach((listener) => listener(data))
  })

  conn.on("JoinedConversation", (conversationId: number) => {
    joinedConversationListeners.forEach((listener) => listener(conversationId))
  })

  conn.onclose((error) => {
    isConnecting = false
  })

  conn.onreconnecting((error) => {
    isConnecting = true
  })

  conn.onreconnected((connectionId) => {
    isConnecting = false
  })

  conn.on("ChannelMessageRead", (data: { messageId: number, readerProfileId: number }) => {
    channelReadReceiptListeners.forEach((listener) => listener(data))
  })


  conn.on("UserConnected", (userId: number) => {
    console.log("[SignalR] Usuario conectado:", userId)
    if (!lastUserIds.includes(userId)) {
      lastUserIds.push(userId)
    }
    activeUsersListeners.forEach((listener) => listener([...lastUserIds]))
  })

  conn.on("UserDisconnected", (userId: number) => {
    console.log("[SignalR] Usuario desconectado:", userId)
    lastUserIds = lastUserIds.filter(id => id !== userId)
    activeUsersListeners.forEach((listener) => listener([...lastUserIds]))
  })

  conn.on("ReceiveActiveUsers", (userIds: number[]) => {
    console.log("[SignalR] Usuarios activos recibidos:", userIds)
    lastUserIds = userIds
    activeUsersListeners.forEach((listener) => listener(userIds))
  })

}

export const initializeWithFallback = async (userId?: string): Promise<boolean> => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    return true
  }

  if (isConnecting && connectionPromise) {
    return connectionPromise
  }

  if (!userId) {
    const storedUserId = localStorage.getItem("userId")
    userId = storedUserId || undefined
  }

  if (!userId) {
    try {
      const userToken = JSON.parse(localStorage.getItem("userToken") || "{}")
      userId = userToken?.user?.id?.toString() || undefined
    } catch (e) {
    }
  }

  if (!userId) {
    return false
  }

  isConnecting = true
  connectionPromise = (async () => {
    try {
      if (connection) {
        await connection.stop()
      }
      console.log("🔍 Conectando SignalR con userId =", userId)
      connection = new signalR.HubConnectionBuilder()
        .withUrl(`${SIGNALR_URL}?profileId=${userId}`, {
          accessTokenFactory: () => {
            const token = localStorage.getItem("authToken") || ""
            return token
          },
          withCredentials: false,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build()

      setupEventHandlers(connection)

      await connection.start()

      try {
        await connection.invoke("IdentifyUser", userId)

        localStorage.setItem("userId", userId)
      } catch (idError) {
      }

      isConnecting = false
      return true
    } catch (error) {
      try {
        if (connection) {
          await connection.stop()
        }

        connection = new signalR.HubConnectionBuilder()
          .withUrl(SIGNALR_URL, {
            accessTokenFactory: () => {
              const token = localStorage.getItem("authToken") || ""
              return token
            },
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            withCredentials: false,
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
          .configureLogging(signalR.LogLevel.Information)
          .build()

        setupEventHandlers(connection)

        await connection.start()

        try {
          await connection.invoke("IdentifyUser", userId)

          localStorage.setItem("userId", userId)
        } catch (idError) {
        }

        isConnecting = false
        return true
      } catch (fallbackError) {
        isConnecting = false
        return false
      }
    }
  })()

  return connectionPromise
}

const ensureConnection = async (): Promise<boolean> => {
  if (!connection) {
    return initializeWithFallback()
  }

  if (connection.state === signalR.HubConnectionState.Connected) {
    return true
  }

  if (connection.state === signalR.HubConnectionState.Disconnected) {
    try {
      await connection.start()
      return true
    } catch (error) {
      return initializeWithFallback()
    }
  }

  return new Promise((resolve) => {
    const checkConnection = () => {
      if (connection?.state === signalR.HubConnectionState.Connected) {
        resolve(true)
      } else if (connection?.state === signalR.HubConnectionState.Disconnected) {
        resolve(false)
      } else {
        setTimeout(checkConnection, 500)
      }
    }
    checkConnection()
  })
}

export const joinConversation = async (conversationId: number, profileId: number): Promise<boolean> => {
  if (!(await ensureConnection())) {
    return false
  }

  try {
    await connection!.invoke("JoinConversation", conversationId, profileId)
    return true
  } catch (error) {
    return false
  }
}

export const sendMessageViaHub = async (
  conversationId: number,
  senderProfileId: number,
  content: string,
  messageType = "Texto",
): Promise<boolean> => {
  if (!(await ensureConnection())) {
    return false
  }

  try {
    await connection!.invoke("SendMessage", conversationId, senderProfileId, content, messageType)
    return true
  } catch (error) {
    return false
  }
}

export const markAsReadViaHub = async (messageId: number, profileId: number): Promise<boolean> => {
  if (!(await ensureConnection())) {
    return false
  }

  try {
    await connection!.invoke("MarkAsRead", messageId, profileId)
    return true
  } catch (error) {
    return false
  }
}

export const getConversationsViaHub = async (profileId: number): Promise<ConversationDto[]> => {
  if (!(await ensureConnection())) {
    return []
  }

  try {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        const index = receiveConversationsListeners.indexOf(tempListener)
        if (index !== -1) {
          receiveConversationsListeners.splice(index, 1)
        }
        resolve([])
      }, 15000)

      const tempListener = (conversations: ConversationDto[]) => {
        clearTimeout(timeout)

        const index = receiveConversationsListeners.indexOf(tempListener)
        if (index !== -1) {
          receiveConversationsListeners.splice(index, 1)
        }

        const conversationIds = conversations.map((c) => c.conversationId)

        // Usar conversationIds para verificar si hay conversaciones duplicadas
        const uniqueIds = [...new Set(conversationIds)]
        if (uniqueIds.length !== conversationIds.length) {
          console.debug(`Found ${conversationIds.length - uniqueIds.length} duplicate conversations`)
        }

        const processedConversations = conversations.map((conv) => ({
          ...conv,
          messages: conv.messages || [],
        }))

        resolve(processedConversations)
      }

      receiveConversationsListeners.push(tempListener)

      connection!
        .invoke("GetConversations", profileId)
        .then(() => {
        })
        .catch((error) => {
          clearTimeout(timeout)

          const index = receiveConversationsListeners.indexOf(tempListener)
          if (index !== -1) {
            receiveConversationsListeners.splice(index, 1)
          }

          resolve([])
        })
    })
  } catch (error) {
    return []
  }
}

export const createConversationViaHub = async (conversationDto: NewConversationDto): Promise<boolean> => {
  if (!(await ensureConnection())) {
    return false
  }

  try {
    await connection!.invoke("CreateConversation", conversationDto)
    return true
  } catch (error) {
    return false
  }
}

export const onReceiveMessage = (handler: MessageHandler): void => {
  if (!messageListeners.includes(handler)) {
    messageListeners.push(handler)
  }
}

export const onNewConversation = (handler: ConversationHandler): void => {
  if (!newConversationListeners.includes(handler)) {
    newConversationListeners.push(handler)
  }
}

export const onReceiveConversations = (handler: ConversationsHandler): void => {
  if (!receiveConversationsListeners.includes(handler)) {
    receiveConversationsListeners.push(handler)
  }
}

export const onMessageRead = (handler: ReadReceiptHandler): void => {
  if (!readReceiptListeners.includes(handler)) {
    readReceiptListeners.push(handler)
  }
}

export const onJoinedConversation = (handler: JoinedConversationHandler): void => {
  if (!joinedConversationListeners.includes(handler)) {
    joinedConversationListeners.push(handler)
  }
}

export const offReceiveMessage = (handler: MessageHandler): void => {
  const index = messageListeners.indexOf(handler)
  if (index !== -1) {
    messageListeners.splice(index, 1)
  }
}

export const offNewConversation = (handler: ConversationHandler): void => {
  const index = newConversationListeners.indexOf(handler)
  if (index !== -1) {
    newConversationListeners.splice(index, 1)
  }
}

export const offReceiveConversations = (handler: ConversationsHandler): void => {
  const index = receiveConversationsListeners.indexOf(handler)
  if (index !== -1) {
    receiveConversationsListeners.splice(index, 1)
  }
}

export const offMessageRead = (handler: ReadReceiptHandler): void => {
  const index = readReceiptListeners.indexOf(handler)
  if (index !== -1) {
    readReceiptListeners.splice(index, 1)
  }
}

export const offJoinedConversation = (handler: JoinedConversationHandler): void => {
  const index = joinedConversationListeners.indexOf(handler)
  if (index !== -1) {
    joinedConversationListeners.splice(index, 1)
  }
}

export const onJoinedChannel = (handler: (data: number) => void): void => {
  if (connection) {
    connection.off("JoinedChannel")
    connection.on("JoinedChannel", (channelId: number) => {
      console.log("[SignalR] Joined channel:", channelId)
      handler(channelId)
    });
  }
}

export const onJoinedGlobalChannel = (handler: (channelId: number) => void): void => {
  if (connection) {
    connection.off("JoinedGlobalChannel")
    connection.on("JoinedGlobalChannel", (channelId: number) => {
      console.log("[SignalR] Joined global channel:", channelId)
      handler(channelId)
    });
  }
}

export const joinChanel = async (channelId: number, profileId: number): Promise<boolean> => {
  if (!(await ensureConnection())) {
    console.error("No hay conexión");
    return false;
  }

  try {
    await connection!.invoke("JoinChannel", channelId, profileId);
    return true;
  } catch (error) {
    console.error("Error al unirse al canal:", error);
    return false;
  }
}

export const joinGlobalChannel = async (channel: string, profileId: number): Promise<boolean> => {
  if (!(await ensureConnection())) {
    console.error("No hay conexión");
    return false;
  }

  try {
    await connection!.invoke("JoinGlobalChannel", channel, profileId);
    return true;
  } catch (error) {
    console.error("Error al unirse al canal global:", error);
    return false;
  }
}

export const sendChannelMessage = async (
  channelId: number,
  senderProfileId: number,
  content: string,
  messageType: string,
  files: File[] = []
): Promise<boolean> => {
  if (!(await ensureConnection())) {
    console.error("No hay conexión");
    return false;
  }
  try {
    let filesToSend: string[] = [];
    if (files.length !== 0) {
      const response = await ChannelService.uploadChatsFiles(files);
      filesToSend = response?.data || [];
    }
    await connection!.invoke("SendChannelMessage", channelId, senderProfileId, content, messageType, filesToSend, null, null, null);
    return true;
  } catch (error) {
    console.error("Error en invoke SendChannelMessage:", error);
    return false;
  }
}

export const markChannelMessageAsRead = async (
  messageId: number,
  profileId: number
): Promise<boolean> => {
  if (!(await ensureConnection())) return false
  try {
    await connection!.invoke("MarkChannelMessageAsRead", messageId, profileId)
    return true
  } catch {
    return false
  }
}

export const sendGlobalMessage = async (
  content: string,
  senderProfileId: number,
  channel: string = "global",
  files: File[] = []
): Promise<boolean> => {
  if (!(await ensureConnection())) {
    console.error("No hay conexión");
    return false;
  }
  try {
    let filesToSend: string[] = [];
    if (files.length !== 0) {
      const response = await ChannelService.uploadChatsFiles(files);
      filesToSend = response?.data || [];
    }
    await connection!.invoke("SendGlobalMessage", content, senderProfileId, channel, filesToSend);
    return true;
  } catch (error) {
    console.error("Error en invoke SendGlobalMessage:", error);
    return false;
  }
}

export const onReceiveChannelMessage = (handler: any): void => {
  if (connection) {
    connection!.off("ReceiveChannelMessage")
    connection!.on("ReceiveChannelMessage", (message: ChannelMessageDto) => {
      console.log("[SignalR] Mensaje recibido directamente en onReceiveChannelMessage:", message)
      handler(message)
    });
  }
}

export const onChannelMessageRead = (handler: ChannelReadReceiptHandler): void => {
  if (!channelReadReceiptListeners.includes(handler)) {
    channelReadReceiptListeners.push(handler)
  }
}

export const onReceiveGlobalMessage = (handler: any): void => {
  
  if(connection){
    connection.off("ReceiveGlobalMessage")
    connection.on("ReceiveGlobalMessage", (messageToSend: GlobalMessageDto) => {
      console.log("[SignalR] Mensaje global recibido directamente en onReceiveGlobalMessage:", messageToSend)
      handler(messageToSend)
    });
  }
}

export const onReceiveVenomMessage = (handler: any): void => {
  if(!ensureConnection()) {
    console.error("No hay conexión para recibir mensajes de Venom")
    return;
  }
  
  connection?.off("ReceiveMessageFromVenom");
  connection?.on("ReceiveMessageFromVenom", (message: ChannelMessageDto) => {
    console.log("[SignalR] Mensaje de Venom recibido:", message)
    handler(message)
  });
}

export const onReceiveGlobalActiveUsers = (handler: any): void => {

  if(connection) {
    connection.off("ReceiveGlobalActiveUsers")
    connection.on("ReceiveGlobalActiveUsers", (users: GlobalUser[]) => {
      console.log("[SignalR] Usuarios globales activos recibidos directamente en onReceiveGlobalActiveUsers:", users)
      handler(users)
    }
    );
  } else {
    console.log("❌ No se pudo registrar handler para usuarios globales activos")
  }
}

export const offReceiveChannelMessage = (handler: ChannelMessageHandler): void => {
  const index = channelMessageListeners.indexOf(handler)
  if (index !== -1) {
    channelMessageListeners.splice(index, 1)
  }
}

export const offReceiveGlobalMessage = (handler: GlobalMessageHandler): void => {
  const index = globalMessageListeners.indexOf(handler)
  if (index !== -1) {
    globalMessageListeners.splice(index, 1)
  }
}

export const onActiveUsersChanged = (handler: ActiveUsersHandler): void => {
  console.log("📥 Registrando nuevo handler para usuarios activos")

  if (!activeUsersListeners.includes(handler)) {
    activeUsersListeners.push(handler)
    console.log("✅ Handler active users registrado. Total handlers:", activeUsersListeners.length)
  } else {
    console.log("⚠️ Handler ya estaba registrado")
  }
}


export const getGlobalActiveUsers = async (): Promise<GlobalUser[]> => {
  console.log("🔍 Entrando a usuarios globales activos...")
  if(connection) {
    const users : GlobalUser[] = await connection!.invoke("GetGlobalActiveUsers")
    console.log("Usuarios globales activos obtenidos:", users)
    return users;
  }
  console.log("❌ No hay conexión para obtener usuarios globales activos")
  return []
}

export const getActiveUsers = async (): Promise<number[]> => {  
  console.log("🔍 Entrando a usuarios activos...")
  if (!(await ensureConnection())) {
    console.log("❌ No hay conexión para obtener usuarios activos")
    return []
  }
  const userIds: number[] = await connection!.invoke("GetActiveUsers")
  console.log("Usuarios activos obtenidosss:", userIds)
  return userIds
}

export const getUsersConnections = async (): Promise<UserConnection[]> => {  
  console.log("🔍 Entrando a usuarios conectados...")
  if (!(await ensureConnection())) {
    console.log("❌ No hay conexión para obtener conexiones de usuarios")
    return []
  }
  const userConnections: UserConnection[] = await connection!.invoke("GetUserConnections")
  console.log("Usuarios conectados obtenidos:", userConnections)
  return userConnections
}

export const offActiveUsersChanged = (handler: ActiveUsersHandler): void => {
  const index = activeUsersListeners.indexOf(handler)
  if (index !== -1) {
    activeUsersListeners.splice(index, 1)
    console.log("🗑️ Handler active users removido. Total restante:", activeUsersListeners.length)
  } else {
    console.log("❌ Handler no estaba registrado para remover")
  }
}

export const disconnectSignalR = async (): Promise<void> => {
  if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
    try {
      await connection.stop()
    } catch (error) {
    } finally {
      connection = null
      connectionPromise = null
      isConnecting = false
    }
  }
}

export const getConnectionState = (): string => {
  if (!connection) return "Not initialized"
  return signalR.HubConnectionState[connection.state]
}

export const clearAllListeners = (): void => {
  messageListeners.length = 0
  newConversationListeners.length = 0
  receiveConversationsListeners.length = 0
  readReceiptListeners.length = 0
  joinedConversationListeners.length = 0
}

export const restartConnection = async (): Promise<boolean> => {
  await disconnectSignalR()
  return initializeWithFallback()
}

export const initializeSignalR = initializeWithFallback